// Inbox.js — full featured: dark ocean bg, right panel, image upload, emoji picker,
// message reactions, send payment link to chat, saved notes, call button,
// 3-dots menu (review/block/report), filter drawer, unread/archived tabs
import React, { useEffect, useState, useRef, useCallback } from 'react';
import dayjs from 'dayjs';

const API_URL = 'https://opmlee572h.execute-api.us-west-2.amazonaws.com';
const POLL_MS = 8000;

const G = {
  panel:  { background: 'rgba(4,16,35,0.82)', backdropFilter: 'blur(40px) saturate(200%)', WebkitBackdropFilter: 'blur(40px) saturate(200%)', border: '1px solid rgba(255,255,255,0.13)' },
  card:   { background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.11)', borderRadius: 12 },
  input:  { background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.14)' },
};

const WAVE_BG = "url('https://images.unsplash.com/photo-1505118380757-91f5f5632de0?w=1800&q=85&auto=format&fit=crop')";
const EMOJI_LIST = ['😊','😂','❤️','👍','🔥','✅','💯','🙏','😍','🤔','💪','🎉','😅','👏','🥳','💬','⭐','✨','😎','🫡','😁','🤙','💥','🏆','👀','🫶','💰','📲','✌️','🤝'];
const REACT_EMOJIS = ['❤️','😂','👍','🔥','😮','😢'];

function Avatar({ name, size = 40, img }) {
  const initials = (name || '?').split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase();
  const COLORS = ['#1e3a8a','#0e7490','#0f766e','#6d28d9','#92400e','#be185d','#1e40af','#065f46'];
  const bg = COLORS[(name?.charCodeAt(0) || 0) % COLORS.length];
  if (img) return <img src={img} alt={name} style={{ width:size, height:size, borderRadius:'50%', objectFit:'cover', flexShrink:0, border:'2px solid rgba(255,255,255,0.2)' }} />;
  return (
    <div style={{ width:size, height:size, borderRadius:'50%', background:bg, display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:800, fontSize:size*0.35, flexShrink:0, border:'2px solid rgba(255,255,255,0.18)', fontFamily:'DM Sans,sans-serif', letterSpacing:'-0.5px' }}>
      {initials}
    </div>
  );
}

const RIGHT_TABS = [
  { key:'profile',  icon:'👤', label:'Profile' },
  { key:'bookings', icon:'📅', label:'Bookings' },
  { key:'payments', icon:'💳', label:'Payments' },
  { key:'notes',    icon:'📝', label:'Notes' },
];

function RightPanel({ activeConv, userId, userType, onSendPaymentLink }) {
  const [tab, setTab] = useState('profile');
  const [clientBookings, setClientBookings] = useState([]);
  const [notes, setNotes] = useState([]);
  const [noteInput, setNoteInput] = useState('');
  const [noteSaved, setNoteSaved] = useState(false);

  const otherId    = activeConv?.otherParty?.id;
  const otherName  = activeConv?.otherParty?.name || 'Client';
  const otherEmail = activeConv?.otherParty?.email || '';
  const otherPic   = activeConv?.otherParty?.profilePicUrl;

  useEffect(() => {
    if (!otherId || !userId) return;
    setClientBookings([]);
    const endpoint = userType === 'provider'
      ? `${API_URL}/bookings?providerId=${userId}`
      : `${API_URL}/client-bookings?clientId=${userId}`;
    fetch(endpoint).then(r => r.json()).then(data => {
      const all = Array.isArray(data) ? data : [];
      setClientBookings(userType === 'provider'
        ? all.filter(b => b.clientId === otherId).slice(-6).reverse()
        : all.filter(b => b.providerId === otherId).slice(-6).reverse()
      );
    }).catch(() => {});
    try {
      const saved = JSON.parse(localStorage.getItem(`inbox_notes_${otherId}`) || '[]');
      setNotes(saved);
    } catch { setNotes([]); }
  }, [otherId, userId, userType]);

  const handleSaveNote = () => {
    if (!noteInput.trim()) return;
    const newNote = { text: noteInput.trim(), date: new Date().toISOString() };
    const updated = [newNote, ...notes];
    setNotes(updated);
    localStorage.setItem(`inbox_notes_${otherId}`, JSON.stringify(updated));
    setNoteInput('');
    setNoteSaved(true);
    setTimeout(() => setNoteSaved(false), 1500);
  };

  const handleDeleteNote = (idx) => {
    const updated = notes.filter((_,i) => i !== idx);
    setNotes(updated);
    localStorage.setItem(`inbox_notes_${otherId}`, JSON.stringify(updated));
  };

  const totalSpent  = clientBookings.filter(b => b.paymentStatus === 'paid').reduce((s,b) => s + parseFloat(b.service?.price||0), 0);
  const lastBooking = clientBookings[0];
  const upcoming    = clientBookings.filter(b => b.date >= new Date().toISOString().split('T')[0] && b.status !== 'cancelled')[0];

  if (!activeConv) return (
    <div style={{ width:272, flexShrink:0, ...G.panel, borderRadius:'0 20px 20px 0', borderLeft:'1px solid rgba(255,255,255,0.09)', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <p style={{ color:'rgba(255,255,255,0.2)', fontSize:'0.75rem', textAlign:'center', padding:'1rem' }}>Select a conversation</p>
    </div>
  );

  return (
    <div style={{ width:272, flexShrink:0, ...G.panel, borderRadius:'0 20px 20px 0', borderLeft:'1px solid rgba(255,255,255,0.09)', display:'flex', flexDirection:'column', overflow:'hidden' }}>
      {/* Header */}
      <div style={{ padding:'1.1rem 0.875rem 0.875rem', borderBottom:'1px solid rgba(255,255,255,0.07)', background:'rgba(255,255,255,0.02)' }}>
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'0.5rem', textAlign:'center' }}>
          <div style={{ position:'relative' }}>
            <Avatar name={otherName} size={62} img={otherPic} />
            <span style={{ position:'absolute', bottom:2, right:2, width:11, height:11, borderRadius:'50%', background:'#4ade80', border:'2px solid rgba(4,16,35,0.95)' }} />
          </div>
          <div>
            <p style={{ fontFamily:'Playfair Display,serif', fontWeight:800, fontSize:'0.92rem', color:'white', marginBottom:'0.08rem' }}>{otherName}</p>
            {otherEmail && <p style={{ fontSize:'0.65rem', color:'rgba(255,255,255,0.32)' }}>{otherEmail}</p>}
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display:'flex', borderBottom:'1px solid rgba(255,255,255,0.07)', flexShrink:0 }}>
        {RIGHT_TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ flex:1, padding:'0.5rem 0', background: tab===t.key?'rgba(255,255,255,0.09)':'transparent', border:'none', borderBottom: tab===t.key?'2px solid rgba(255,255,255,0.5)':'2px solid transparent', color: tab===t.key?'white':'rgba(255,255,255,0.32)', cursor:'pointer', fontSize:'0.56rem', fontWeight:700, fontFamily:'DM Sans,sans-serif', transition:'all 0.15s', display:'flex', flexDirection:'column', alignItems:'center', gap:'2px', textTransform:'uppercase', letterSpacing:'0.04em' }}>
            <span style={{ fontSize:'0.82rem' }}>{t.icon}</span>
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex:1, overflowY:'auto', padding:'0.75rem' }}>

        {tab === 'profile' && (
          <div style={{ display:'flex', flexDirection:'column', gap:'0.55rem' }}>
            <div style={{ ...G.card, padding:'0.8rem' }}>
              <p style={{ fontSize:'0.58rem', fontWeight:700, color:'rgba(255,255,255,0.35)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'0.55rem' }}>Client Overview</p>
              {[
                { label:'Total Bookings', value: clientBookings.length || '—' },
                { label:'Total Spent',    value: totalSpent>0 ? `$${totalSpent.toFixed(2)}` : '—' },
                { label:'Last Booking',   value: lastBooking ? dayjs(lastBooking.date).format('MMM D, YYYY') : '—' },
                { label:'Member Since',   value: activeConv?.createdAt ? dayjs(activeConv.createdAt).format('MMM D, YYYY') : '—' },
              ].map((row,i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', marginBottom:i<3?'0.38rem':0 }}>
                  <span style={{ fontSize:'0.7rem', color:'rgba(255,255,255,0.38)' }}>{row.label}</span>
                  <span style={{ fontSize:'0.7rem', fontWeight:700, color:'white' }}>{row.value}</span>
                </div>
              ))}
            </div>

            {upcoming && (
              <div style={{ background:'rgba(59,130,246,0.11)', border:'1px solid rgba(59,130,246,0.22)', borderRadius:12, padding:'0.8rem' }}>
                <p style={{ fontSize:'0.58rem', fontWeight:700, color:'rgba(255,255,255,0.35)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'0.45rem' }}>Upcoming Booking</p>
                <div style={{ display:'flex', alignItems:'center', gap:'0.45rem', marginBottom:'0.45rem' }}>
                  <div style={{ width:28, height:28, borderRadius:7, background:'rgba(59,130,246,0.22)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.8rem', flexShrink:0 }}>📅</div>
                  <div>
                    <p style={{ fontSize:'0.76rem', fontWeight:700, color:'white', marginBottom:'0.03rem' }}>{dayjs(upcoming.date).format('ddd, MMM D')}</p>
                    <p style={{ fontSize:'0.64rem', color:'rgba(255,255,255,0.45)' }}>{upcoming.slot} · {upcoming.service?.name}</p>
                  </div>
                  <span style={{ marginLeft:'auto', background:'rgba(74,222,128,0.16)', color:'#4ade80', border:'1px solid rgba(74,222,128,0.26)', borderRadius:20, fontSize:'0.55rem', fontWeight:800, padding:'2px 6px', flexShrink:0 }}>✓ Confirmed</span>
                </div>
              </div>
            )}

            <div style={{ ...G.card, padding:'0.8rem' }}>
              <p style={{ fontSize:'0.58rem', fontWeight:700, color:'rgba(255,255,255,0.35)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'0.45rem' }}>Quick Actions</p>
              {[
                { icon:'💳', label:'Send Payment Link', action: () => onSendPaymentLink?.() },
                { icon:'📝', label:'Add Note',          action: () => setTab('notes') },
                { icon:'📅', label:'View Bookings',     action: () => setTab('bookings') },
              ].map((item,i) => (
                <button key={i} onClick={item.action}
                  style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, padding:'0.42rem 0.65rem', marginBottom:i<2?'0.3rem':0, color:'rgba(255,255,255,0.72)', fontSize:'0.73rem', fontWeight:600, cursor:'pointer', fontFamily:'DM Sans,sans-serif', transition:'all 0.15s', textAlign:'left' }}
                  onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,0.1)'; e.currentTarget.style.color='white'; }}
                  onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.05)'; e.currentTarget.style.color='rgba(255,255,255,0.72)'; }}>
                  <span style={{ display:'flex', alignItems:'center', gap:'0.4rem' }}><span>{item.icon}</span>{item.label}</span>
                  <span style={{ color:'rgba(255,255,255,0.22)', fontSize:'0.82rem' }}>›</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {tab === 'bookings' && (
          <div style={{ display:'flex', flexDirection:'column', gap:'0.38rem' }}>
            <p style={{ fontSize:'0.58rem', fontWeight:700, color:'rgba(255,255,255,0.35)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'0.2rem' }}>Booking History</p>
            {clientBookings.length === 0
              ? <div style={{ textAlign:'center', padding:'2rem 1rem', color:'rgba(255,255,255,0.22)', fontSize:'0.72rem' }}><div style={{ fontSize:'1.4rem', marginBottom:'0.5rem' }}>📭</div>No bookings yet</div>
              : clientBookings.map((b,i) => (
                <div key={i} style={{ ...G.card, padding:'0.65rem' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'0.2rem' }}>
                    <p style={{ fontSize:'0.76rem', fontWeight:700, color:'white' }}>{b.service?.name||'Service'}</p>
                    <span style={{ fontSize:'0.76rem', fontWeight:800, color:'#7fffd4', fontFamily:'Playfair Display,serif' }}>${b.service?.price}</span>
                  </div>
                  <p style={{ fontSize:'0.63rem', color:'rgba(255,255,255,0.35)' }}>{dayjs(b.date).format('MMM D, YYYY')} · {b.slot}</p>
                  <div style={{ marginTop:'0.28rem' }}>
                    <span style={{ fontSize:'0.56rem', fontWeight:700, padding:'1px 6px', borderRadius:20, background: b.paymentStatus==='paid'?'rgba(74,222,128,0.13)':'rgba(251,191,36,0.13)', color: b.paymentStatus==='paid'?'#4ade80':'#fbbf24', border:`1px solid ${b.paymentStatus==='paid'?'rgba(74,222,128,0.25)':'rgba(251,191,36,0.25)'}` }}>
                      {b.paymentStatus==='paid'?'Paid':b.paymentStatus==='pay_in_person'?'In Person':'Pending'}
                    </span>
                  </div>
                </div>
              ))
            }
          </div>
        )}

        {tab === 'payments' && (
          <div style={{ display:'flex', flexDirection:'column', gap:'0.55rem' }}>
            <div style={{ ...G.card, padding:'0.8rem' }}>
              <p style={{ fontSize:'0.58rem', fontWeight:700, color:'rgba(255,255,255,0.35)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'0.55rem' }}>Payment Summary</p>
              {[
                { label:'Total Paid',    value:`$${totalSpent.toFixed(2)}`, color:'#7fffd4' },
                { label:'Bookings',      value: clientBookings.length, color:'white' },
                { label:'Avg per visit', value: clientBookings.length>0?`$${(totalSpent/clientBookings.length).toFixed(2)}`:'—', color:'white' },
              ].map((row,i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', marginBottom:i<2?'0.38rem':0 }}>
                  <span style={{ fontSize:'0.7rem', color:'rgba(255,255,255,0.38)' }}>{row.label}</span>
                  <span style={{ fontSize:'0.76rem', fontWeight:800, color:row.color, fontFamily:'Playfair Display,serif' }}>{row.value}</span>
                </div>
              ))}
            </div>
            <button onClick={() => onSendPaymentLink?.()}
              style={{ width:'100%', padding:'0.65rem', background:'rgba(59,130,246,0.68)', border:'1px solid rgba(59,130,246,0.45)', borderRadius:10, color:'white', fontSize:'0.76rem', fontWeight:700, cursor:'pointer', fontFamily:'DM Sans,sans-serif', transition:'all 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.background='rgba(59,130,246,0.88)'}
              onMouseLeave={e => e.currentTarget.style.background='rgba(59,130,246,0.68)'}>
              💳 Send Payment Link to Chat
            </button>
          </div>
        )}

        {tab === 'notes' && (
          <div style={{ display:'flex', flexDirection:'column', gap:'0.55rem' }}>
            <div>
              <p style={{ fontSize:'0.58rem', fontWeight:700, color:'rgba(255,255,255,0.35)', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'0.2rem' }}>Private Notes</p>
              <p style={{ fontSize:'0.65rem', color:'rgba(255,255,255,0.28)', lineHeight:1.5, marginBottom:'0.5rem' }}>Only you can see these.</p>
            </div>
            <textarea value={noteInput} onChange={e => setNoteInput(e.target.value)}
              placeholder={`Note about ${otherName}...`} rows={4}
              onKeyDown={e => { if (e.key==='Enter' && (e.metaKey||e.ctrlKey)) handleSaveNote(); }}
              style={{ width:'100%', background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:10, padding:'0.65rem', color:'white', fontSize:'0.76rem', fontFamily:'DM Sans,sans-serif', resize:'vertical', outline:'none', lineHeight:1.6, boxSizing:'border-box' }}
              onFocus={e => e.target.style.borderColor='rgba(255,255,255,0.32)'}
              onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.12)'}
            />
            <button onClick={handleSaveNote} disabled={!noteInput.trim()}
              style={{ padding:'0.5rem', background: noteInput.trim() ? (noteSaved?'rgba(74,222,128,0.25)':'rgba(255,255,255,0.13)') : 'rgba(255,255,255,0.04)', border:`1px solid ${noteInput.trim()?'rgba(255,255,255,0.18)':'rgba(255,255,255,0.07)'}`, borderRadius:8, color: noteInput.trim()?'white':'rgba(255,255,255,0.25)', fontSize:'0.73rem', fontWeight:700, cursor: noteInput.trim()?'pointer':'not-allowed', fontFamily:'DM Sans,sans-serif', transition:'all 0.2s' }}>
              {noteSaved ? '✓ Note Saved!' : '+ Save Note'}
            </button>
            {notes.length > 0 && (
              <div style={{ display:'flex', flexDirection:'column', gap:'0.38rem' }}>
                <p style={{ fontSize:'0.58rem', fontWeight:700, color:'rgba(255,255,255,0.28)', textTransform:'uppercase', letterSpacing:'0.06em' }}>Saved Notes ({notes.length})</p>
                {notes.map((n,i) => (
                  <div key={i} style={{ ...G.card, padding:'0.65rem', position:'relative' }}
                    onMouseEnter={e => e.currentTarget.querySelector('.del-note').style.opacity='1'}
                    onMouseLeave={e => e.currentTarget.querySelector('.del-note').style.opacity='0'}>
                    <p style={{ fontSize:'0.73rem', color:'rgba(255,255,255,0.75)', lineHeight:1.55, marginBottom:'0.28rem', paddingRight:'1.25rem' }}>{n.text}</p>
                    <p style={{ fontSize:'0.58rem', color:'rgba(255,255,255,0.25)' }}>{dayjs(n.date).format('MMM D, h:mm A')}</p>
                    <button className="del-note" onClick={() => handleDeleteNote(i)}
                      style={{ position:'absolute', top:5, right:5, background:'rgba(220,38,38,0.65)', border:'none', borderRadius:'50%', width:17, height:17, color:'white', fontSize:'0.52rem', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', opacity:0, transition:'opacity 0.15s' }}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Inbox({ userId, userName, userEmail, userType, initialConversationId }) {
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId]   = useState(initialConversationId || null);
  const [messages, setMessages]           = useState([]);
  const [draft, setDraft]                 = useState('');
  const [sending, setSending]             = useState(false);
  const [loadingConvs, setLoadingConvs]   = useState(true);
  const [loadingMsgs, setLoadingMsgs]     = useState(false);
  const [search, setSearch]               = useState('');
  const [filter, setFilter]               = useState('all');
  const [showFilters, setShowFilters]     = useState(false);
  const [filterSort, setFilterSort]       = useState('recent');
  const [filterType, setFilterType]       = useState('all');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showDotMenu, setShowDotMenu]     = useState(false);
  const [reactingTo, setReactingTo]       = useState(null);
  const [reactions, setReactions]         = useState({});
  const [archivedConvs, setArchivedConvs] = useState(() => { try { return JSON.parse(localStorage.getItem('inbox_archived')||'[]'); } catch { return []; } });
  const [reviewModal, setReviewModal]     = useState(false);
  const [reviewRating, setReviewRating]   = useState(0);
  const [reviewText, setReviewText]       = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef        = useRef(null);
  const textareaRef    = useRef(null);
  const fileInputRef   = useRef(null);
  const dotMenuRef     = useRef(null);
  const emojiRef       = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const res  = await fetch(`${API_URL}/conversations?userId=${userId}`);
      const data = await res.json();
      const all  = Array.isArray(data) ? data : [];
      const fil  = all.filter(conv => {
        if (userType==='client')   return conv.otherParty?.type==='provider';
        if (userType==='provider') return conv.otherParty?.type==='client';
        return true;
      });
      setConversations(fil.sort((a,b) => new Date(b.lastMessage?.createdAt||0)-new Date(a.lastMessage?.createdAt||0)));
    } catch {}
    setLoadingConvs(false);
  }, [userId, userType]);

  const fetchMessages = useCallback(async (convId) => {
    if (!convId) return;
    setLoadingMsgs(true);
    try {
      const res  = await fetch(`${API_URL}/messages?conversationId=${encodeURIComponent(convId)}`);
      const data = await res.json();
      setMessages(Array.isArray(data) ? data : []);
      await fetch(`${API_URL}/messages/read`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ conversationId:convId, userId }) });
      setConversations(prev => prev.map(c => c.conversationId===convId ? {...c,unreadCount:0} : c));
    } catch {}
    setLoadingMsgs(false);
  }, [userId]);

  useEffect(() => { if (userId) fetchConversations(); }, [userId, fetchConversations]);
  useEffect(() => { if (activeConvId) fetchMessages(activeConvId); }, [activeConvId, fetchMessages]);
  useEffect(() => {
    if (!userId) return;
    pollRef.current = setInterval(() => { fetchConversations(); if (activeConvId) fetchMessages(activeConvId); }, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [userId, activeConvId, fetchConversations, fetchMessages]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior:'smooth' }); }, [messages]);

  useEffect(() => {
    const handler = (e) => {
      if (dotMenuRef.current && !dotMenuRef.current.contains(e.target)) setShowDotMenu(false);
      if (emojiRef.current && !emojiRef.current.contains(e.target)) setShowEmojiPicker(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSend = async (text = null) => {
    const msg = text !== null ? text : draft;
    if (!msg.trim() || !activeConvId) return;
    const activeConv = conversations.find(c => c.conversationId===activeConvId);
    if (!activeConv) return;
    const opt = { messageId:`tmp-${Date.now()}`, senderId:userId, senderName:userName, text:msg.trim(), createdAt:new Date().toISOString(), pending:true };
    setMessages(prev => [...prev, opt]);
    if (text === null) setDraft('');
    setSending(true);
    try {
      await fetch(`${API_URL}/messages`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ senderId:userId, recipientId:activeConv.otherParty.id, senderName:userName, recipientName:activeConv.otherParty.name, senderType:userType, text:opt.text, recipientEmail:activeConv.otherParty.email||'' }) });
      await fetchMessages(activeConvId);
      await fetchConversations();
    } catch {}
    setSending(false);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e) => { if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  const handleSendPaymentLink = () => {
    const link = `${window.location.origin}/provider/${userId}`;
    handleSend(`💳 Here's my booking & payment link: ${link}`);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    if (!['image/jpeg','image/png','image/webp','image/gif'].includes(file.type)) { alert('Only JPG, PNG, WebP or GIF allowed.'); return; }
    if (file.size > 8*1024*1024) { alert('Image must be under 8MB.'); return; }
    setUploadingImage(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          const base64 = ev.target.result.split(',')[1];
          const res  = await fetch(`${API_URL}/upload-image`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ imageData:base64, contentType:file.type, userId }) });
          const data = await res.json();
          if (data.publicUrl) await handleSend(`[img]${data.publicUrl}[/img]`);
        } catch { alert('Upload failed.'); }
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch { setUploadingImage(false); }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleReact = (msgId, emoji) => {
    setReactions(prev => {
      const existing = prev[msgId] || [];
      return { ...prev, [msgId]: existing.includes(emoji) ? existing.filter(e=>e!==emoji) : [...existing, emoji] };
    });
    setReactingTo(null);
  };

  const handleArchive = (convId) => {
    const updated = archivedConvs.includes(convId) ? archivedConvs.filter(id=>id!==convId) : [...archivedConvs, convId];
    setArchivedConvs(updated);
    localStorage.setItem('inbox_archived', JSON.stringify(updated));
    setShowDotMenu(false);
  };

  const handleBlockClient = () => {
    if (window.confirm(`Block ${activeConv?.otherParty?.name}? They won't be able to message you.`)) alert('Client blocked.');
    setShowDotMenu(false);
  };

  const handleReport = () => { alert('Report submitted. Our team will review within 24 hours.'); setShowDotMenu(false); };

  const handleReviewSubmit = async () => {
    if (!reviewRating) return;
    const activeConv = conversations.find(c => c.conversationId===activeConvId);
    if (!activeConv) return;
    try { await fetch(`${API_URL}/rate-client`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ clientId:activeConv.otherParty.id, providerId:userId, rating:reviewRating, note:reviewText }) }); } catch {}
    setReviewModal(false); setReviewRating(0); setReviewText('');
    alert('Review saved!');
  };

  const totalUnread   = conversations.filter(c=>!archivedConvs.includes(c.conversationId)).reduce((s,c)=>s+(c.unreadCount||0),0);
  const activeConv    = conversations.find(c=>c.conversationId===activeConvId);
  const isProvider    = userType==='provider';
  const unreadCount   = conversations.filter(c=>!archivedConvs.includes(c.conversationId)&&c.unreadCount>0).length;
  const archivedCount = archivedConvs.length;

  const fmtTime = (iso) => {
    if (!iso) return '';
    const d = dayjs(iso);
    if (d.isSame(dayjs(),'day')) return d.format('h:mm A');
    if (d.isSame(dayjs().subtract(1,'day'),'day')) return 'Yesterday';
    return d.format('MMM D');
  };

  let visibleConvs = conversations.filter(c => {
    const name = (c.otherParty?.name||'').toLowerCase();
    const last = (c.lastMessage?.text||'').toLowerCase();
    const matchSearch = !search || name.includes(search.toLowerCase()) || last.includes(search.toLowerCase());
    const isArchived  = archivedConvs.includes(c.conversationId);
    if (filter==='archived') return matchSearch && isArchived;
    if (filter==='unread')   return matchSearch && !isArchived && c.unreadCount>0;
    return matchSearch && !isArchived;
  });
  if (filterType!=='all')    visibleConvs = visibleConvs.filter(c=>c.otherParty?.type===filterType);
  if (filterSort==='az')     visibleConvs = [...visibleConvs].sort((a,b)=>(a.otherParty?.name||'').localeCompare(b.otherParty?.name||''));
  if (filterSort==='oldest') visibleConvs = [...visibleConvs].sort((a,b)=>new Date(a.lastMessage?.createdAt||0)-new Date(b.lastMessage?.createdAt||0));

  const renderMsgContent = (msg) => {
    const isMe = msg.senderId===userId;
    const imgMatch = msg.text?.match(/\[img\](.*?)\[\/img\]/);
    return (
      <div style={{ position:'relative' }}
        onMouseEnter={e=>{ const btn=e.currentTarget.querySelector('.react-btn'); if(btn) btn.style.opacity='1'; }}
        onMouseLeave={e=>{ const btn=e.currentTarget.querySelector('.react-btn'); if(btn) btn.style.opacity='0'; }}>
        {imgMatch ? (
          <img src={imgMatch[1]} alt="shared" onClick={()=>window.open(imgMatch[1],'_blank')}
            style={{ maxWidth:220, maxHeight:200, borderRadius:12, objectFit:'cover', display:'block', border:'1px solid rgba(255,255,255,0.14)', cursor:'zoom-in' }} />
        ) : (
          <div style={{ padding:'0.58rem 0.875rem', borderRadius:isMe?'18px 18px 4px 18px':'18px 18px 18px 4px', background:isMe?'rgba(59,130,246,0.82)':'rgba(255,255,255,0.12)', backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(12px)', border:isMe?'1px solid rgba(59,130,246,0.45)':'1px solid rgba(255,255,255,0.14)', color:'white', fontSize:'0.875rem', lineHeight:1.55, opacity:msg.pending?0.55:1, boxShadow:isMe?'0 4px 14px rgba(59,130,246,0.28)':'0 2px 8px rgba(0,0,0,0.18)', maxWidth:300, wordBreak:'break-word' }}>
            {msg.text}
          </div>
        )}
        {(reactions[msg.messageId]||[]).length>0 && (
          <div style={{ display:'flex', gap:'3px', marginTop:'3px', justifyContent:isMe?'flex-end':'flex-start', flexWrap:'wrap' }}>
            {[...new Set(reactions[msg.messageId])].map((emoji,i)=>(
              <span key={i} onClick={()=>handleReact(msg.messageId,emoji)}
                style={{ background:'rgba(255,255,255,0.11)', border:'1px solid rgba(255,255,255,0.17)', borderRadius:20, padding:'1px 6px', fontSize:'0.76rem', cursor:'pointer' }}>
                {emoji}
              </span>
            ))}
          </div>
        )}
        <button className="react-btn" onClick={()=>setReactingTo(reactingTo===msg.messageId?null:msg.messageId)}
          style={{ position:'absolute', top:-8, [isMe?'left':'right']:-26, background:'rgba(15,25,50,0.92)', border:'1px solid rgba(255,255,255,0.14)', borderRadius:20, padding:'1px 6px', fontSize:'0.68rem', cursor:'pointer', opacity:0, transition:'opacity 0.15s', color:'white' }}>
          😊
        </button>
        {reactingTo===msg.messageId && (
          <div style={{ position:'absolute', [isMe?'left':'right']:0, bottom:'100%', marginBottom:4, background:'rgba(10,20,45,0.97)', backdropFilter:'blur(20px)', border:'1px solid rgba(255,255,255,0.16)', borderRadius:24, padding:'5px 7px', display:'flex', gap:'3px', zIndex:200, boxShadow:'0 8px 24px rgba(0,0,0,0.45)' }}>
            {REACT_EMOJIS.map(emoji=>(
              <span key={emoji} onClick={()=>handleReact(msg.messageId,emoji)}
                style={{ fontSize:'1.18rem', cursor:'pointer', padding:'2px 3px', borderRadius:6, transition:'transform 0.1s' }}
                onMouseEnter={e=>e.currentTarget.style.transform='scale(1.3)'}
                onMouseLeave={e=>e.currentTarget.style.transform='scale(1)'}>
                {emoji}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'0.875rem', height:'calc(100vh - 160px)', minHeight:520 }}>

      {/* Top bar */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
        <div>
          <h1 style={{ fontFamily:'Playfair Display,serif', fontSize:'1.75rem', fontWeight:800, color:'white', letterSpacing:'-0.5px', textShadow:'0 2px 12px rgba(0,0,0,0.2)', display:'flex', alignItems:'center', gap:'0.75rem' }}>
            💬 Inbox
            {totalUnread>0 && <span style={{ background:'rgba(59,130,246,0.82)', color:'white', borderRadius:99, fontSize:'0.68rem', fontWeight:800, padding:'2px 9px' }}>{totalUnread}</span>}
          </h1>
          <p style={{ color:'rgba(255,255,255,0.42)', fontSize:'0.78rem', marginTop:'0.1rem' }}>{isProvider?'Messages from your clients':'Messages from your providers'}</p>
        </div>
        <div style={{ display:'flex', gap:'0.38rem' }}>
          {[
            { v:'all',      l:`All ${conversations.filter(c=>!archivedConvs.includes(c.conversationId)).length}` },
            { v:'unread',   l:`Unread ${unreadCount}` },
            { v:'archived', l:`Archived ${archivedCount}` },
          ].map(f=>(
            <button key={f.v} onClick={()=>setFilter(f.v)}
              style={{ ...G.input, borderRadius:8, padding:'0.38rem 0.82rem', color:filter===f.v?'white':'rgba(255,255,255,0.42)', fontWeight:filter===f.v?700:500, fontSize:'0.74rem', cursor:'pointer', fontFamily:'DM Sans,sans-serif', transition:'all 0.15s', background:filter===f.v?'rgba(255,255,255,0.2)':'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.13)' }}>
              {f.l}
            </button>
          ))}
        </div>
      </div>

      {/* Three-panel container */}
      <div style={{ display:'grid', gridTemplateColumns:'262px 1fr 272px', flex:1, minHeight:0, overflow:'hidden', borderRadius:20, border:'1px solid rgba(255,255,255,0.11)', position:'relative', backgroundImage:WAVE_BG, backgroundSize:'cover', backgroundPosition:'center' }}>
        {/* Dark overlay */}
        <div style={{ position:'absolute', inset:0, background:'rgba(2,8,22,0.62)', borderRadius:20, pointerEvents:'none', zIndex:0 }} />

        {/* LEFT */}
        <div style={{ ...G.panel, borderRadius:'20px 0 0 20px', overflow:'hidden', display:'flex', flexDirection:'column', borderRight:'1px solid rgba(255,255,255,0.09)', position:'relative', zIndex:1 }}>
          {/* Search + filter button */}
          <div style={{ padding:'0.7rem', borderBottom:'1px solid rgba(255,255,255,0.07)', flexShrink:0 }}>
            <div style={{ display:'flex', gap:'0.4rem', alignItems:'center' }}>
              <div style={{ ...G.input, borderRadius:10, padding:'0.42rem 0.7rem', display:'flex', alignItems:'center', gap:'0.4rem', flex:1 }}>
                <span style={{ color:'rgba(255,255,255,0.28)', fontSize:'0.8rem', flexShrink:0 }}>🔍</span>
                <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search conversations..."
                  style={{ flex:1, background:'transparent', border:'none', outline:'none', color:'white', fontSize:'0.76rem', fontFamily:'DM Sans,sans-serif' }} />
                {search && <button onClick={()=>setSearch('')} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.3)', cursor:'pointer', fontSize:'0.72rem', padding:0, flexShrink:0 }}>✕</button>}
              </div>
              <button onClick={()=>setShowFilters(!showFilters)} title="Filters"
                style={{ width:32, height:32, borderRadius:9, background:showFilters?'rgba(255,255,255,0.18)':'rgba(255,255,255,0.07)', border:`1px solid ${showFilters?'rgba(255,255,255,0.3)':'rgba(255,255,255,0.12)'}`, color:showFilters?'white':'rgba(255,255,255,0.45)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.82rem', flexShrink:0, transition:'all 0.15s' }}>
                ⚙
              </button>
            </div>
            {showFilters && (
              <div style={{ marginTop:'0.55rem', background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.09)', borderRadius:10, padding:'0.65rem', display:'flex', flexDirection:'column', gap:'0.55rem' }}>
                <div>
                  <p style={{ fontSize:'0.57rem', fontWeight:700, color:'rgba(255,255,255,0.32)', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:'0.3rem' }}>Sort by</p>
                  <div style={{ display:'flex', gap:'0.28rem' }}>
                    {[{v:'recent',l:'Recent'},{v:'oldest',l:'Oldest'},{v:'az',l:'A–Z'}].map(s=>(
                      <button key={s.v} onClick={()=>setFilterSort(s.v)}
                        style={{ flex:1, padding:'0.28rem', borderRadius:7, background:filterSort===s.v?'rgba(255,255,255,0.16)':'rgba(255,255,255,0.05)', border:`1px solid ${filterSort===s.v?'rgba(255,255,255,0.28)':'rgba(255,255,255,0.09)'}`, color:filterSort===s.v?'white':'rgba(255,255,255,0.42)', fontSize:'0.62rem', fontWeight:700, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                        {s.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p style={{ fontSize:'0.57rem', fontWeight:700, color:'rgba(255,255,255,0.32)', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:'0.3rem' }}>Show</p>
                  <div style={{ display:'flex', gap:'0.28rem' }}>
                    {[{v:'all',l:'All'},{v:'client',l:'Clients'},{v:'provider',l:'Providers'}].map(s=>(
                      <button key={s.v} onClick={()=>setFilterType(s.v)}
                        style={{ flex:1, padding:'0.28rem', borderRadius:7, background:filterType===s.v?'rgba(255,255,255,0.16)':'rgba(255,255,255,0.05)', border:`1px solid ${filterType===s.v?'rgba(255,255,255,0.28)':'rgba(255,255,255,0.09)'}`, color:filterType===s.v?'white':'rgba(255,255,255,0.42)', fontSize:'0.62rem', fontWeight:700, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>
                        {s.l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* List */}
          <div style={{ flex:1, overflowY:'auto' }}>
            {loadingConvs ? (
              <div style={{ padding:'2rem', textAlign:'center', color:'rgba(255,255,255,0.28)', fontSize:'0.82rem' }}>Loading...</div>
            ) : visibleConvs.length===0 ? (
              <div style={{ padding:'3rem 1.5rem', textAlign:'center' }}>
                <div style={{ fontSize:'2.2rem', marginBottom:'0.625rem', opacity:0.22 }}>💬</div>
                <p style={{ fontFamily:'Playfair Display,serif', fontWeight:800, color:'white', fontSize:'0.85rem', marginBottom:'0.28rem' }}>
                  {filter==='archived'?'No archived chats':filter==='unread'?'All caught up!':'No messages yet'}
                </p>
                <p style={{ color:'rgba(255,255,255,0.3)', fontSize:'0.72rem', lineHeight:1.5 }}>{isProvider?'Clients will message you after booking':'Book a service to start chatting'}</p>
              </div>
            ) : visibleConvs.map(conv => {
              const isActive  = conv.conversationId===activeConvId;
              const hasUnread = conv.unreadCount>0;
              const lastText  = conv.lastMessage?.text||'';
              const isMine    = conv.lastMessage?.senderId===userId;
              return (
                <div key={conv.conversationId} onClick={()=>setActiveConvId(conv.conversationId)}
                  style={{ display:'flex', alignItems:'center', gap:'0.7rem', padding:'0.7rem 0.82rem', cursor:'pointer', transition:'all 0.14s', background:isActive?'rgba(255,255,255,0.13)':'transparent', borderLeft:isActive?'3px solid rgba(255,255,255,0.5)':'3px solid transparent', borderBottom:'1px solid rgba(255,255,255,0.055)' }}
                  onMouseEnter={e=>{ if(!isActive) e.currentTarget.style.background='rgba(255,255,255,0.055)'; }}
                  onMouseLeave={e=>{ if(!isActive) e.currentTarget.style.background='transparent'; }}>
                  <div style={{ position:'relative', flexShrink:0 }}>
                    <Avatar name={conv.otherParty?.name} size={39} img={conv.otherParty?.profilePicUrl} />
                    <span style={{ position:'absolute', bottom:0, right:0, width:8, height:8, borderRadius:'50%', background:'#4ade80', border:'2px solid rgba(4,16,35,0.95)' }} />
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.16rem' }}>
                      <p style={{ fontWeight:hasUnread?800:600, fontSize:'0.82rem', color:'white', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{conv.otherParty?.name||'Unknown'}</p>
                      <span style={{ fontSize:'0.58rem', color:'rgba(255,255,255,0.28)', flexShrink:0, marginLeft:'0.35rem' }}>{fmtTime(conv.lastMessage?.createdAt)}</span>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.28rem' }}>
                      <p style={{ fontSize:'0.7rem', color:hasUnread?'rgba(255,255,255,0.62)':'rgba(255,255,255,0.3)', fontWeight:hasUnread?600:400, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }}>
                        {isMine?'You: ':''}{lastText.slice(0,42)}{lastText.length>42?'...':''}
                      </p>
                      {hasUnread && <span style={{ background:'#3b82f6', color:'white', borderRadius:99, fontSize:'0.56rem', fontWeight:800, padding:'1px 6px', minWidth:16, textAlign:'center', flexShrink:0 }}>{conv.unreadCount}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CENTER */}
        <div style={{ background:'rgba(2,10,26,0.65)', overflow:'hidden', display:'flex', flexDirection:'column', position:'relative', zIndex:1 }}>
          {!activeConvId ? (
            <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:'0.625rem' }}>
              <div style={{ fontSize:'3.2rem', filter:'drop-shadow(0 0 20px rgba(100,180,255,0.22))' }}>💬</div>
              <p style={{ fontFamily:'Playfair Display,serif', fontSize:'1.05rem', fontWeight:800, color:'white' }}>Select a conversation</p>
              <p style={{ color:'rgba(255,255,255,0.32)', fontSize:'0.8rem' }}>Choose one from the left to read and reply</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0.7rem 0.95rem', borderBottom:'1px solid rgba(255,255,255,0.08)', background:'rgba(255,255,255,0.035)', flexShrink:0 }}>
                <div style={{ display:'flex', alignItems:'center', gap:'0.7rem' }}>
                  <div style={{ position:'relative' }}>
                    <Avatar name={activeConv?.otherParty?.name} size={34} img={activeConv?.otherParty?.profilePicUrl} />
                    <span style={{ position:'absolute', bottom:0, right:0, width:7, height:7, borderRadius:'50%', background:'#4ade80', border:'2px solid rgba(2,10,26,0.95)' }} />
                  </div>
                  <div>
                    <p style={{ fontWeight:700, fontSize:'0.845rem', color:'white', marginBottom:'0.06rem' }}>{activeConv?.otherParty?.name||'Conversation'}</p>
                    <p style={{ fontSize:'0.64rem', color:'#4ade80', fontWeight:600 }}>● Online</p>
                  </div>
                </div>
                <div style={{ display:'flex', gap:'0.38rem', alignItems:'center' }}>
                  {/* Phone call */}
                  {activeConv?.otherParty?.phone ? (
                    <a href={`tel:${activeConv.otherParty.phone}`}
                      style={{ width:30, height:30, borderRadius:'50%', background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.12)', color:'rgba(255,255,255,0.6)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.8rem', textDecoration:'none', transition:'all 0.15s' }}
                      onMouseEnter={e=>e.currentTarget.style.background='rgba(74,222,128,0.18)'}
                      onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.08)'}>
                      📞
                    </a>
                  ) : (
                    <button title="No phone number on file" style={{ width:30, height:30, borderRadius:'50%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.09)', color:'rgba(255,255,255,0.25)', cursor:'not-allowed', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.8rem' }}>📞</button>
                  )}
                  {/* 3-dot menu */}
                  <div style={{ position:'relative' }} ref={dotMenuRef}>
                    <button onClick={()=>setShowDotMenu(!showDotMenu)}
                      style={{ width:30, height:30, borderRadius:'50%', background:showDotMenu?'rgba(255,255,255,0.16)':'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.12)', color:'rgba(255,255,255,0.65)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.95rem', transition:'all 0.14s' }}>
                      ⋯
                    </button>
                    {showDotMenu && (
                      <div style={{ position:'absolute', top:'calc(100% + 5px)', right:0, background:'rgba(6,16,40,0.98)', backdropFilter:'blur(24px)', border:'1px solid rgba(255,255,255,0.14)', borderRadius:12, minWidth:175, zIndex:300, boxShadow:'0 8px 32px rgba(0,0,0,0.55)', overflow:'hidden' }}>
                        {[
                          { icon:'⭐', label:'Review Client',   action:()=>{ setReviewModal(true); setShowDotMenu(false); } },
                          { icon:'📦', label: archivedConvs.includes(activeConvId)?'Unarchive':'Archive Chat', action:()=>handleArchive(activeConvId) },
                          { icon:'🚫', label:'Block Client',    action:handleBlockClient, danger:false },
                          { icon:'🚩', label:'Report',          action:handleReport, danger:true },
                        ].map((item,i)=>(
                          <button key={i} onClick={item.action}
                            style={{ width:'100%', display:'flex', alignItems:'center', gap:'0.55rem', padding:'0.65rem 0.95rem', background:'transparent', border:'none', borderBottom:i<3?'1px solid rgba(255,255,255,0.065)':'none', color:item.danger?'#fca5a5':'rgba(255,255,255,0.8)', fontSize:'0.8rem', fontWeight:600, cursor:'pointer', fontFamily:'DM Sans,sans-serif', transition:'background 0.14s', textAlign:'left' }}
                            onMouseEnter={e=>e.currentTarget.style.background=item.danger?'rgba(220,38,38,0.14)':'rgba(255,255,255,0.075)'}
                            onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                            <span>{item.icon}</span>{item.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div style={{ flex:1, overflowY:'auto', padding:'0.875rem', display:'flex', flexDirection:'column', gap:'0.45rem' }}>
                {loadingMsgs && messages.length===0 ? (
                  <div style={{ textAlign:'center', color:'rgba(255,255,255,0.28)', fontSize:'0.82rem', padding:'3rem' }}>Loading...</div>
                ) : messages.length===0 ? (
                  <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'0.45rem', padding:'3rem' }}>
                    <span style={{ fontSize:'1.8rem', opacity:0.3 }}>👋</span>
                    <p style={{ color:'rgba(255,255,255,0.3)', fontSize:'0.82rem' }}>No messages yet — say hello!</p>
                  </div>
                ) : messages.map((msg,i)=>{
                  const isMe     = msg.senderId===userId;
                  const showDate = i===0 || dayjs(msg.createdAt).format('YYYY-MM-DD')!==dayjs(messages[i-1].createdAt).format('YYYY-MM-DD');
                  const showAva  = !isMe && (i===0 || messages[i-1].senderId!==msg.senderId);
                  return (
                    <React.Fragment key={msg.messageId||i}>
                      {showDate && (
                        <div style={{ display:'flex', alignItems:'center', gap:'0.55rem', margin:'0.35rem 0' }}>
                          <div style={{ flex:1, height:1, background:'rgba(255,255,255,0.07)' }} />
                          <span style={{ fontSize:'0.6rem', color:'rgba(255,255,255,0.28)', fontWeight:600, whiteSpace:'nowrap' }}>
                            {dayjs(msg.createdAt).isSame(dayjs(),'day')?'Today':dayjs(msg.createdAt).format('MMM D, YYYY')}
                          </span>
                          <div style={{ flex:1, height:1, background:'rgba(255,255,255,0.07)' }} />
                        </div>
                      )}
                      <div style={{ display:'flex', justifyContent:isMe?'flex-end':'flex-start', alignItems:'flex-end', gap:'0.38rem' }}>
                        {!isMe && (showAva ? <Avatar name={activeConv?.otherParty?.name} size={23} img={activeConv?.otherParty?.profilePicUrl} /> : <div style={{ width:23, flexShrink:0 }} />)}
                        <div style={{ maxWidth:'66%' }}>
                          {msg.aiGenerated && <p style={{ fontSize:'0.57rem', color:'rgba(100,220,200,0.7)', fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', marginBottom:'0.12rem', textAlign:'right' }}>🤖 AI Reply</p>}
                          {renderMsgContent(msg)}
                          <p style={{ fontSize:'0.56rem', color:'rgba(255,255,255,0.26)', marginTop:'0.18rem', textAlign:isMe?'right':'left', display:'flex', alignItems:'center', justifyContent:isMe?'flex-end':'flex-start', gap:'0.18rem' }}>
                            {dayjs(msg.createdAt).format('h:mm A')}
                            {isMe && !msg.pending && <span style={{ color:'rgba(100,160,255,0.55)' }}>✓✓</span>}
                          </p>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Compose */}
              <div style={{ padding:'0.65rem 0.82rem', borderTop:'1px solid rgba(255,255,255,0.08)', background:'rgba(255,255,255,0.035)', flexShrink:0 }}>
                {showEmojiPicker && (
                  <div ref={emojiRef} style={{ marginBottom:'0.45rem', background:'rgba(6,16,40,0.97)', backdropFilter:'blur(24px)', border:'1px solid rgba(255,255,255,0.13)', borderRadius:14, padding:'0.55rem', display:'flex', flexWrap:'wrap', gap:'3px', maxWidth:260 }}>
                    {EMOJI_LIST.map(e=>(
                      <span key={e} onClick={()=>{ setDraft(prev=>prev+e); setShowEmojiPicker(false); textareaRef.current?.focus(); }}
                        style={{ fontSize:'1.15rem', cursor:'pointer', padding:'3px', borderRadius:6, transition:'transform 0.1s' }}
                        onMouseEnter={ev=>ev.currentTarget.style.transform='scale(1.22)'}
                        onMouseLeave={ev=>ev.currentTarget.style.transform='scale(1)'}>
                        {e}
                      </span>
                    ))}
                  </div>
                )}
                <div style={{ display:'flex', alignItems:'flex-end', gap:'0.42rem', ...G.input, borderRadius:15, padding:'0.4rem 0.45rem 0.4rem 0.82rem' }}>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} style={{ display:'none' }} />
                  <button onClick={()=>fileInputRef.current?.click()} disabled={uploadingImage}
                    style={{ background:'transparent', border:'none', color:uploadingImage?'rgba(255,255,255,0.18)':'rgba(255,255,255,0.28)', cursor:uploadingImage?'not-allowed':'pointer', fontSize:'0.95rem', padding:'0.18rem', lineHeight:1, transition:'color 0.14s', flexShrink:0 }}
                    onMouseEnter={e=>{ if(!uploadingImage) e.currentTarget.style.color='rgba(255,255,255,0.65)'; }}
                    onMouseLeave={e=>e.currentTarget.style.color='rgba(255,255,255,0.28)'}>
                    {uploadingImage?'⏳':'📎'}
                  </button>
                  <button onClick={()=>setShowEmojiPicker(!showEmojiPicker)}
                    style={{ background:'transparent', border:'none', color:showEmojiPicker?'rgba(255,255,255,0.75)':'rgba(255,255,255,0.28)', cursor:'pointer', fontSize:'0.95rem', padding:'0.18rem', lineHeight:1, transition:'color 0.14s', flexShrink:0 }}>
                    😊
                  </button>
                  <textarea ref={textareaRef} value={draft} onChange={e=>setDraft(e.target.value)} onKeyDown={handleKeyDown}
                    placeholder="Type a message... (Enter to send)"
                    rows={1}
                    style={{ flex:1, background:'transparent', border:'none', outline:'none', color:'white', fontSize:'0.855rem', fontFamily:'DM Sans,sans-serif', resize:'none', lineHeight:1.55, maxHeight:110, overflowY:'auto', paddingTop:'0.22rem' }}
                    onInput={e=>{ e.target.style.height='auto'; e.target.style.height=Math.min(e.target.scrollHeight,110)+'px'; }}
                  />
                  <button onClick={()=>handleSend()} disabled={!draft.trim()||sending}
                    style={{ width:32, height:32, borderRadius:'50%', border:'none', cursor:draft.trim()?'pointer':'not-allowed', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.9rem', transition:'all 0.18s', background:draft.trim()?'rgba(59,130,246,0.88)':'rgba(255,255,255,0.08)', color:'white', boxShadow:draft.trim()?'0 3px 12px rgba(59,130,246,0.38)':'none', flexShrink:0 }}
                    onMouseEnter={e=>{ if(draft.trim()) e.currentTarget.style.background='rgba(59,130,246,1)'; }}
                    onMouseLeave={e=>{ if(draft.trim()) e.currentTarget.style.background='rgba(59,130,246,0.88)'; }}>
                    {sending?'⏳':'➤'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* RIGHT */}
        <div style={{ position:'relative', zIndex:1 }}>
          <RightPanel activeConv={activeConv} userId={userId} userType={userType} onSendPaymentLink={handleSendPaymentLink} />
        </div>
      </div>

      {/* Review modal */}
      {reviewModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.78)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000, padding:'1rem', backdropFilter:'blur(8px)' }}>
          <div style={{ background:'rgba(6,16,40,0.98)', backdropFilter:'blur(32px)', border:'1px solid rgba(255,255,255,0.16)', borderRadius:20, padding:'2rem', width:'100%', maxWidth:400, boxShadow:'0 24px 60px rgba(0,0,0,0.55)' }}>
            <h2 style={{ fontFamily:'Playfair Display,serif', fontSize:'1.15rem', fontWeight:800, color:'white', marginBottom:'0.2rem' }}>Review {activeConv?.otherParty?.name}</h2>
            <p style={{ fontSize:'0.76rem', color:'rgba(255,255,255,0.4)', marginBottom:'1.1rem' }}>Visible to other providers.</p>
            <div style={{ display:'flex', gap:'0.22rem', marginBottom:'0.875rem' }}>
              {[1,2,3,4,5].map(s=>(
                <span key={s} onClick={()=>setReviewRating(s)}
                  style={{ fontSize:'1.65rem', cursor:'pointer', color:s<=reviewRating?'#d97706':'rgba(255,255,255,0.18)', transition:'color 0.1s, transform 0.1s' }}
                  onMouseEnter={e=>e.currentTarget.style.transform='scale(1.2)'}
                  onMouseLeave={e=>e.currentTarget.style.transform='scale(1)'}>★</span>
              ))}
            </div>
            <textarea value={reviewText} onChange={e=>setReviewText(e.target.value)} placeholder="What was it like working with this client?" rows={4}
              style={{ width:'100%', background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.13)', borderRadius:10, padding:'0.7rem', color:'white', fontSize:'0.8rem', fontFamily:'DM Sans,sans-serif', resize:'vertical', outline:'none', boxSizing:'border-box', marginBottom:'0.875rem' }} />
            <div style={{ display:'flex', gap:'0.65rem' }}>
              <button onClick={()=>{ setReviewModal(false); setReviewRating(0); setReviewText(''); }}
                style={{ flex:1, padding:'0.7rem', background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.13)', borderRadius:9, color:'rgba(255,255,255,0.65)', fontWeight:600, cursor:'pointer', fontFamily:'DM Sans,sans-serif' }}>Cancel</button>
              <button onClick={handleReviewSubmit} disabled={!reviewRating}
                style={{ flex:2, padding:'0.7rem', background:reviewRating?'var(--brand-1)':'rgba(255,255,255,0.07)', border:'none', borderRadius:9, color:reviewRating?'white':'rgba(255,255,255,0.28)', fontWeight:700, cursor:reviewRating?'pointer':'not-allowed', fontFamily:'DM Sans,sans-serif' }}>
                Submit Review
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        ::-webkit-scrollbar{width:3px;height:3px}
        ::-webkit-scrollbar-track{background:transparent}
        ::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.13);border-radius:99px}
        ::-webkit-scrollbar-thumb:hover{background:rgba(255,255,255,0.25)}
      `}</style>
    </div>
  );
}